import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button, Input } from '../../../ui';
import {
  UserGroupIcon,
  QrCodeIcon,
  LinkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface SponsorStepProps {
  hasSponsor: boolean | null;
  sponsorInviteCode: string | null;
  onSubmit: (hasSponsor: boolean, inviteCode?: string) => void;
}

export function SponsorStep({
  hasSponsor: initialHasSponsor,
  sponsorInviteCode: initialCode,
  onSubmit,
}: SponsorStepProps) {
  const [hasSponsor, setHasSponsor] = useState<boolean | null>(initialHasSponsor);
  const [inviteCode, setInviteCode] = useState(initialCode || '');
  const [showCodeInput, setShowCodeInput] = useState(!!initialCode);

  const handleSubmit = () => {
    if (hasSponsor === null) return;
    onSubmit(hasSponsor, hasSponsor && inviteCode ? inviteCode : undefined);
  };

  return (
    <div>
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <p className="text-surface-600 dark:text-surface-400">
          Connecting with a sponsor helps you stay accountable. You can always set this up later.
        </p>
      </motion.div>

      <motion.div
        className="space-y-4 mb-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {/* I have a sponsor */}
        <button
          type="button"
          onClick={() => {
            setHasSponsor(true);
            setShowCodeInput(true);
          }}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
            hasSponsor === true
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-md'
              : 'border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 hover:border-surface-300'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`p-2.5 rounded-xl ${
                hasSponsor === true
                  ? 'bg-teal-100 dark:bg-teal-800/30'
                  : 'bg-surface-100 dark:bg-surface-700'
              }`}
            >
              <UserGroupIcon
                className={`w-6 h-6 ${
                  hasSponsor === true ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500'
                }`}
              />
            </div>
            <div>
              <h3 className="font-semibold text-surface-800 dark:text-white">I have a sponsor</h3>
              <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
                Connect with your sponsor using their invite code or QR
              </p>
            </div>
          </div>
        </button>

        {/* Invite code input */}
        {hasSponsor === true && showCodeInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-surface-50 dark:bg-surface-800/50 rounded-xl p-4 border border-surface-200 dark:border-surface-700"
          >
            <p className="text-sm text-surface-600 dark:text-surface-400 mb-4">
              Ask your sponsor for their invite code or scan their QR code
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Enter invite code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                />
              </div>
              <Button variant="secondary" className="flex items-center gap-2">
                <QrCodeIcon className="w-5 h-5" />
                <span className="hidden sm:inline">Scan QR</span>
              </Button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-surface-500">
              <LinkIcon className="w-4 h-4" />
              <span>Or paste a sponsor invite link</span>
            </div>
          </motion.div>
        )}

        {/* I don't have a sponsor */}
        <button
          type="button"
          onClick={() => {
            setHasSponsor(false);
            setShowCodeInput(false);
            setInviteCode('');
          }}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
            hasSponsor === false
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-md'
              : 'border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 hover:border-surface-300'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`p-2.5 rounded-xl ${
                hasSponsor === false
                  ? 'bg-teal-100 dark:bg-teal-800/30'
                  : 'bg-surface-100 dark:bg-surface-700'
              }`}
            >
              <UserGroupIcon
                className={`w-6 h-6 ${
                  hasSponsor === false ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500'
                }`}
              />
            </div>
            <div>
              <h3 className="font-semibold text-surface-800 dark:text-white">
                I don't have a sponsor yet
              </h3>
              <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
                That's okay! You can connect with a sponsor anytime
              </p>
            </div>
          </div>
        </button>
      </motion.div>

      {/* Helpful tip for those who have a sponsor but don't have info handy */}
      <motion.div
        className="mb-6 p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-primary-800 dark:text-primary-200 font-medium">
              Don't have your sponsor's info handy?
            </p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
              No problem! Select "I don't have a sponsor yet" to continue, and we'll remind you to
              add their information in a few days. You can always add or update sponsor details from
              your profile settings.
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="pt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <Button onClick={handleSubmit} size="lg" className="w-full" disabled={hasSponsor === null}>
          Continue
        </Button>
      </motion.div>
    </div>
  );
}
